import { Link } from "react-router";
import { useShallow } from "zustand/react/shallow";
import { WORLD_MANIFEST, districtOfProperty } from "@townsy/world";
import { useTownSession } from "../../app/TownSessionContext";
import { districtName } from "../../lib/directoryFilters";
import { OUT_OF_CAR_FIRST } from "../../net/vehicles";
import { useTownStore } from "../../state/store";
import { useVisitorsInside } from "../VisitorAccess";
import { MyHomeManager } from "./MyHomeManager";
import { useHomeActions, useHomeData } from "./useHomeData";

/**
 * The My home manager inside the 3D town (HUD "My home", the owner's property panel, the account
 * page). It takes the left side panel; the yard ghost shows on the street (the overview camera
 * frames the home), furniture is placed from inside the home.
 *
 * World v5 (ADR-0041): it names the home's district. ADR-0042 (one continuous town): every home is on the one town
 * street, so the Yard tab frames the yard from anywhere on the street (`showYard`), whichever district it is in.
 */
export function MyHomePanel() {
  const session = useTownSession();
  const { open, tab, roomKind, roomPropertyId, roomKey, sessionId, sceneStatus, selected, chatOpen, pending, driving } = useTownStore(
    useShallow((s) => ({
      open: s.ui.myHomeOpen,
      tab: s.ui.myHomeTab,
      roomKind: s.room.kind,
      roomPropertyId: s.room.propertyId,
      roomKey: s.room.roomKey,
      sessionId: s.room.sessionId,
      sceneStatus: s.ui.sceneStatus,
      selected: s.ui.selectedPropertyId,
      chatOpen: s.ui.chatOpen,
      pending: s.ui.transitionPending,
      driving: s.ui.drivingVehicleId !== null,
    })),
  );
  const setUi = useTownStore((s) => s.setUi);
  const data = useHomeData(open);
  const actions = useHomeActions(session);
  // ADR-0019: the owner inside their home sees how many others are there (from this client's own room state).
  const visitorsInside = useVisitorsInside(data.home?.propertyId ?? null);
  const small = typeof window !== "undefined" && window.innerWidth <= 900;
  if (!open || (small && selected)) return null;

  const home = data.home;
  const officeId = session?.realEstateOfficeId ?? null;
  const close = () => setUi({ myHomeOpen: false });
  const homeDistrict = home ? districtOfProperty(WORLD_MANIFEST, home.propertyId) : null;

  return (
    <section className={`panel panel-left real-estate-panel my-home-panel${chatOpen ? " compact" : ""}`} role="region" aria-labelledby="my-home-title" data-testid="my-home-panel">
      <div className="sheet-handle" aria-hidden="true" />
      <div className="panel-header">
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 id="my-home-title">My home</h2>
          <div className="small muted">Place, rotate and remove your items. Homes can never be taken over by other players.</div>
        </div>
        <button type="button" className="btn btn-ghost icon-btn" aria-label="Close My home" onClick={close}>
          ×
        </button>
      </div>
      <div className="panel-body">
        {!data.signedIn ? (
          <div className="notice notice-info small">
            <Link to="/account">Sign in</Link> to manage your home.
          </div>
        ) : data.homeState === "loading" || (home && data.inventoryState === "loading") ? (
          <p className="muted">Loading your home…</p>
        ) : data.homeState === "error" ? (
          <div className="notice notice-danger" role="alert">
            Your home could not be loaded. Try again in a moment.
          </div>
        ) : !home ? (
          <div className="notice notice-info small" data-testid="my-home-none">
            You don't own a home yet. Buy a plot at Townsy Real Estate, 1 Plaza Way.{" "}
            {officeId ? (
              <button
                type="button"
                className="btn btn-sm"
                disabled={driving}
                title={driving ? OUT_OF_CAR_FIRST : undefined}
                onClick={() => void session?.fastTravel(officeId)}
              >
                Go to Townsy Real Estate
              </button>
            ) : null}
          </div>
        ) : (
          <>
            <MyHomeDistrict homeDistrict={homeDistrict} />
            <MyHomeManager
              home={home}
              inventory={data.inventory}
              catalogItems={data.catalog?.items ?? null}
              manifest={WORLD_MANIFEST}
              tab={tab}
              onTab={(next) => setUi({ myHomeTab: next })}
              actions={actions}
              visitorsInside={visitorsInside}
              sink={session?.previewSink ?? null}
              epoch={`${roomKey ?? "none"}|${sessionId ?? "none"}|${sceneStatus}`}
              town={
                session
                  ? {
                      insideHome: roomKind === "interior" && roomPropertyId === home.propertyId,
                      // One town street (ADR-0042): the yard can be framed from anywhere on it.
                      onStreet: roomKind === "street",
                      transitionPending: pending,
                      travelBlockedReason: driving ? OUT_OF_CAR_FIRST : null,
                      onEnterHome: () => void session.enterHome(home.propertyId),
                      onShowYard: () => session.showYard(home.propertyId),
                    }
                  : null
              }
              shopAction={(shop) => (
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={driving}
                  title={driving ? OUT_OF_CAR_FIRST : undefined}
                  onClick={() => session?.openShop(shop)}
                  data-testid={`go-to-${shop}`}
                >
                  {shop === "home_builders" ? "Buy at Townsy Home Builders" : "Buy at Townsy Furnishings"}
                </button>
              )}
            />
          </>
        )}
      </div>
    </section>
  );
}

/** "Your home is in Riverside." (world v5; ADR-0042: the same wherever the visitor is, since it is one town). */
export function MyHomeDistrict({ homeDistrict }: { homeDistrict: string | null }) {
  if (!homeDistrict) return null;
  return (
    <p className="small" style={{ margin: "0 0 8px" }} data-testid="my-home-district">
      Your home is in <strong>{districtName(homeDistrict)}</strong>.
    </p>
  );
}
